import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import BackToTop from "../components/BackToTop";
import Reveal from "../components/Reveal";
import SpiderChart from "../components/SpiderChart";

const AgencyLandingPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Body scroll lock + ESC handler while the mobile menu is open.
  useEffect(() => {
    if (!mobileMenuOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") setMobileMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [mobileMenuOpen]);

  const engineItems = [
    "Strategy & Positioning",
    "GTM & Marketing",
    "Sales Funnel & Conversions",
    "CRM & Data Discipline",
    "Team & KPIs",
    "Pricing Architecture",
    "BD & Channels",
    "After-Sales",
  ];

  // Inline SVG icons for the 8 pillars (24x24, chrome metallic with soft glow)
  const pillarIcons = [
    // Strategy & Positioning — target/crosshair
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" filter="url(#ralChromeGlow)" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="5.5" />
        <circle cx="12" cy="12" r="2" fill="url(#ralChrome)" />
        <line x1="12" y1="1.5" x2="12" y2="4" />
        <line x1="12" y1="20" x2="12" y2="22.5" />
        <line x1="1.5" y1="12" x2="4" y2="12" />
        <line x1="20" y1="12" x2="22.5" y2="12" />
      </svg>
    ),
    // GTM & Marketing — megaphone
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" filter="url(#ralChromeGlow)" aria-hidden="true">
        <path d="M3 11v2a1 1 0 0 0 1 1h3l8 5V5L7 10H4a1 1 0 0 0-1 1z" />
        <path d="M18.5 8.5a4 4 0 0 1 0 7" />
      </svg>
    ),
    // Sales Funnel & Conversions — funnel
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" filter="url(#ralChromeGlow)" aria-hidden="true">
        <path d="M3 4h18l-7 9v7l-4-2v-5z" />
      </svg>
    ),
    // CRM & Data Discipline — database
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" filter="url(#ralChromeGlow)" aria-hidden="true">
        <ellipse cx="12" cy="5" rx="8" ry="2.5" />
        <path d="M4 5v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5V5" />
        <path d="M4 11v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5v-6" />
      </svg>
    ),
    // Team & KPIs — users / group
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" filter="url(#ralChromeGlow)" aria-hidden="true">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="3.5" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    // Pricing Architecture — tag with euro
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" filter="url(#ralChromeGlow)" aria-hidden="true">
        <path d="M20.5 11L13 18.5a2 2 0 0 1-2.83 0L3 11.33V2.5h8.83L20.5 11a2 2 0 0 1 0 2.83z" />
        <circle cx="7.5" cy="7" r="1.2" fill="url(#ralChrome)" />
        <path d="M14.2 11.6c-.7-.5-1.6-.8-2.5-.6-1.4.3-2.3 1.6-2 3 .3 1.4 1.6 2.3 3 2 .8-.2 1.4-.7 1.7-1.4M11 13.4h2.6M11 15h2.4" />
      </svg>
    ),
    // BD & Channels — share/network
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" filter="url(#ralChromeGlow)" aria-hidden="true">
        <circle cx="18" cy="5" r="2.5" fill="url(#ralChrome)" fillOpacity="0.15" />
        <circle cx="6" cy="12" r="2.5" fill="url(#ralChrome)" fillOpacity="0.15" />
        <circle cx="18" cy="19" r="2.5" fill="url(#ralChrome)" fillOpacity="0.15" />
        <line x1="8.2" y1="10.8" x2="15.8" y2="6.2" />
        <line x1="8.2" y1="13.2" x2="15.8" y2="17.8" />
      </svg>
    ),
    // After-Sales — shield with check
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" filter="url(#ralChromeGlow)" aria-hidden="true">
        <path d="M12 2L4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  ];

  // Lighter variant for compact contexts (hero card boxes) — chrome stroke, no glow
  const pillarIconsLite = [
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="5.5" />
        <circle cx="12" cy="12" r="2" fill="url(#ralChrome)" />
        <line x1="12" y1="1.5" x2="12" y2="4" />
        <line x1="12" y1="20" x2="12" y2="22.5" />
        <line x1="1.5" y1="12" x2="4" y2="12" />
        <line x1="20" y1="12" x2="22.5" y2="12" />
      </svg>
    ),
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 11v2a1 1 0 0 0 1 1h3l8 5V5L7 10H4a1 1 0 0 0-1 1z" />
        <path d="M18.5 8.5a4 4 0 0 1 0 7" />
      </svg>
    ),
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 4h18l-7 9v7l-4-2v-5z" />
      </svg>
    ),
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <ellipse cx="12" cy="5" rx="8" ry="2.5" />
        <path d="M4 5v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5V5" />
        <path d="M4 11v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5v-6" />
      </svg>
    ),
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="3.5" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20.5 11L13 18.5a2 2 0 0 1-2.83 0L3 11.33V2.5h8.83L20.5 11a2 2 0 0 1 0 2.83z" />
        <circle cx="7.5" cy="7" r="1.2" fill="url(#ralChrome)" />
        <path d="M14.2 11.6c-.7-.5-1.6-.8-2.5-.6-1.4.3-2.3 1.6-2 3 .3 1.4 1.6 2.3 3 2 .8-.2 1.4-.7 1.7-1.4M11 13.4h2.6M11 15h2.4" />
      </svg>
    ),
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="18" cy="5" r="2.5" fill="url(#ralChrome)" fillOpacity="0.15" />
        <circle cx="6" cy="12" r="2.5" fill="url(#ralChrome)" fillOpacity="0.15" />
        <circle cx="18" cy="19" r="2.5" fill="url(#ralChrome)" fillOpacity="0.15" />
        <line x1="8.2" y1="10.8" x2="15.8" y2="6.2" />
        <line x1="8.2" y1="13.2" x2="15.8" y2="17.8" />
      </svg>
    ),
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 2L4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  ];

  const systemPhases = [
    "Strategy & Market Intelligence",
    "Marketing Operating System",
    "Sales Team Enablement",
    "CRM & Data Governance",
    "Pricing & Revenue Architecture",
    "GTM Execution",
    "Optimisation",
  ];

  const transformItems = [
    "Lead quality",
    "Lead → View → Offer → Sales conversion",
    "CRM adoption",
    "Team KPIs",
    "Pricing & EQ logic",
    "Agent performance",
    "Funnel efficiency",
  ];

  const deliverables = [
    "Diagnostic Report",
    "Strategic: GTM, segmentation, EQ pricing",
    "Sales: Playbook, scripts, KPIs",
    "Marketing: Funnel design, campaigns, dashboards",
    "CRM: Pipeline, rules, data processes",
    "Channel: Agent scoring, BD activation",
  ];

  const workItems = [
    "Weekly performance calls",
    "On-site / remote enablement",
    "Funnel & marketing planning",
    "CRM re-architecture",
    "Sales & agent workshops",
    "Leadership collaboration",
  ];

  const engagementModels = [
    "Project-Based Advisory",
    "Optimisation",
    "Monthly Advisory Retainer",
    "Leadership Coaching",
    "Team Enablement Packages",
  ];

  return (
    <>
      <SEOHead page="home" />

      {/* Shared SVG defs (chrome gradient + soft glow) used by pillar icons */}
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <linearGradient id="ralChrome" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="35%" stopColor="#C4CDD9" />
            <stop offset="65%" stopColor="#9DB4E8" />
            <stop offset="100%" stopColor="#5B7BA8" />
          </linearGradient>
          <filter id="ralChromeGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      </svg>

      <div className="min-h-screen bg-white text-white overflow-hidden scroll-smooth">
        {/* Global dark background */}
        <div className="fixed inset-0 -z-10">
          <div className="absolute inset-0 bg-[#05070d]" />
        </div>

        {/* Navbar */}
        <header
          className={`sticky top-0 z-[100] transition-all duration-300 ${
            scrolled
              ? "bg-[#05070d]/95 backdrop-blur-md border-b border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
              : "bg-[#05070d] border-b border-white/10"
          }`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 md:py-5 flex items-center justify-between gap-3">
            {/* Brand mark — logo + wordmark.
                Mobile: smaller logo, smaller single-line wordmark, no tagline.
                Desktop (md+): full size, two-line layout with tagline. */}
            <div className="flex items-center gap-2 md:gap-3 min-w-0">
              <img
                src="http://agency.hubst3r.com/wp-content/uploads/2026/03/logo.png"
                alt="RAL Real Advisor"
                className="w-9 h-9 md:w-12 md:h-12 object-contain shrink-0"
              />
              <div className="min-w-0">
                <div className="text-sm md:text-lg font-serif font-semibold leading-none whitespace-nowrap">
                  RAL Real Advisor
                </div>
                <div className="hidden md:block text-xs uppercase tracking-[0.28em] text-blue-200 mt-1">
                  Commercial Optimisation
                </div>
              </div>
            </div>

            {/* Desktop nav — hidden on mobile */}
            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/contact"
                className="border border-white/20 px-5 py-3 text-sm uppercase tracking-[0.22em] hover:bg-white/10 transition"
              >
                Contact
              </Link>
              <Link
                to="/diagnostic"
                className="border border-white/20 px-5 py-3 text-sm uppercase tracking-[0.22em] hover:bg-white/10 transition"
              >
                Diagnostic Tool
              </Link>
              <Link
                to="/login"
                className="border border-white/20 px-5 py-3 text-sm uppercase tracking-[0.22em] hover:bg-white/10 transition"
              >
                Sign In
              </Link>
            </div>

            {/* Hamburger button — visible only on mobile */}
            <button
              type="button"
              aria-label="Open menu"
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-white/20 hover:bg-white/10 transition shrink-0"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
                aria-hidden="true"
              >
                <line x1="4" y1="7" x2="20" y2="7" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="17" x2="20" y2="17" />
              </svg>
            </button>
          </div>
        </header>

        {/* Mobile menu overlay — visible only on mobile when mobileMenuOpen is true.
            Sits above content (z-[110] vs navbar's z-[100]). The navbar's
            hamburger button toggles `mobileMenuOpen`, body scroll is locked
            while open, and ESC closes it (see effect at top of component). */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 z-[110] md:hidden bg-[#05070d]/95 backdrop-blur-md flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile menu"
            onClick={() => setMobileMenuOpen(false)}
          >
            {/* Top bar inside overlay — repeats brand + close button so the user
                doesn't lose context. Click events stop here so taps on the bar
                don't dismiss the menu. */}
            <div
              className="flex items-center justify-between px-6 py-4 border-b border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 min-w-0">
                <img
                  src="http://agency.hubst3r.com/wp-content/uploads/2026/03/logo.png"
                  alt="RAL Real Advisor"
                  className="w-9 h-9 object-contain shrink-0"
                />
                <div className="text-sm font-serif font-semibold leading-none whitespace-nowrap">
                  RAL Real Advisor
                </div>
              </div>
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setMobileMenuOpen(false)}
                className="inline-flex items-center justify-center w-10 h-10 border border-white/20 hover:bg-white/10 transition shrink-0"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-5 h-5"
                  aria-hidden="true"
                >
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="6" y1="18" x2="18" y2="6" />
                </svg>
              </button>
            </div>

            {/* Menu items. Each closes the menu on tap. Click events stop on
                the nav container so backdrop taps elsewhere still dismiss. */}
            <nav
              className="flex-1 flex flex-col px-6 py-8 gap-3 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="border border-white/20 px-5 py-4 text-sm uppercase tracking-[0.22em] hover:bg-white/10 transition text-center"
              >
                Contact
              </Link>
              <Link
                to="/diagnostic"
                onClick={() => setMobileMenuOpen(false)}
                className="border border-white/20 px-5 py-4 text-sm uppercase tracking-[0.22em] hover:bg-white/10 transition text-center"
              >
                Diagnostic Tool
              </Link>
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="border border-white/20 px-5 py-4 text-sm uppercase tracking-[0.22em] hover:bg-white/10 transition text-center"
              >
                Sign In
              </Link>
            </nav>

            {/* Footer hint inside overlay — small affordance reminding ESC works.
                Hidden by default on touch devices, visible on hover-capable screens. */}
            <div
              className="px-6 py-4 border-t border-white/10 text-xs text-neutral-500 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              Tap outside or press ESC to close
            </div>
          </div>
        )}

        {/* Hero */}
        <section
          id="top"
          className="relative min-h-[calc(100vh-92px)] flex items-center pt-14 pb-24 lg:pt-20 lg:pb-28 bg-[#05070d] overflow-hidden scroll-mt-28"
        >
          {/* Dot grid */}
          <div className="absolute inset-0 opacity-[0.08]">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
                backgroundSize: "40px 40px",
              }}
            />
          </div>

          {/* Horizontal blueprint lines */}
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(0deg, #9fc6ff 0, #9fc6ff 1px, transparent 1px, transparent 120px)",
            }}
          />

          {/* Conic light source (top-right) */}
          <div
            className="absolute -top-32 -right-32 w-[700px] h-[700px] opacity-30 pointer-events-none"
            style={{
              background:
                "conic-gradient(from 200deg at 50% 50%, transparent 0deg, rgba(159,198,255,0.18) 60deg, transparent 140deg)",
              filter: "blur(40px)",
            }}
          />

          {/* Soft glows */}
          <div className="absolute top-24 right-0 w-[520px] h-[520px] bg-blue-400/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-1/4 w-[420px] h-[420px] bg-blue-300/10 rounded-full blur-3xl pointer-events-none" />

          {/* Vignette - soft dark edges */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at center, transparent 40%, rgba(5,7,13,0.7) 100%)",
            }}
          />

          {/* Architectural silhouettes at bottom - layered for depth */}
          {/* Far layer (faintest) */}
          <svg
            className="absolute bottom-0 left-0 w-full h-44 opacity-[0.12] pointer-events-none"
            viewBox="0 0 1440 180"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <g fill="#9fc6ff" stroke="none">
              <path d="M0 180 L0 130 L40 130 L40 100 L90 100 L90 115 L140 115 L140 85 L200 85 L200 105 L250 105 L250 75 L310 75 L310 95 L360 95 L360 70 L420 70 L420 110 L480 110 L480 80 L540 80 L540 95 L600 95 L600 65 L660 65 L660 90 L720 90 L720 100 L780 100 L780 70 L840 70 L840 95 L900 95 L900 80 L960 80 L960 105 L1020 105 L1020 75 L1080 75 L1080 100 L1140 100 L1140 85 L1200 85 L1200 110 L1260 110 L1260 90 L1320 90 L1320 70 L1380 70 L1380 105 L1440 105 L1440 180 Z" />
            </g>
          </svg>

          {/* Mid layer (silhouette outline) */}
          <svg
            className="absolute bottom-0 left-0 w-full h-40 opacity-[0.28] pointer-events-none"
            viewBox="0 0 1440 160"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <g fill="none" stroke="#9fc6ff" strokeWidth="1.2">
              <path d="M0 140 L60 140 L60 95 L120 95 L120 110 L180 110 L180 70 L240 70 L240 90 L300 90 L300 120 L360 120 L360 80 L420 80 L420 105 L480 105 L480 60 L540 60 L540 100 L600 100 L600 130 L660 130 L660 75 L720 75 L720 95 L780 95 L780 50 L840 50 L840 85 L900 85 L900 115 L960 115 L960 70 L1020 70 L1020 100 L1080 100 L1080 125 L1140 125 L1140 80 L1200 80 L1200 110 L1260 110 L1260 65 L1320 65 L1320 95 L1380 95 L1380 130 L1440 130 L1440 160 L0 160 Z" />
              {/* Window highlights along skyline */}
              <line x1="120"  y1="100" x2="120"  y2="105" strokeOpacity="1" strokeWidth="2" />
              <line x1="180"  y1="80"  x2="180"  y2="85"  strokeOpacity="1" strokeWidth="2" />
              <line x1="180"  y1="95"  x2="180"  y2="100" strokeOpacity="1" strokeWidth="2" />
              <line x1="480"  y1="70"  x2="480"  y2="75"  strokeOpacity="1" strokeWidth="2" />
              <line x1="480"  y1="85"  x2="480"  y2="90"  strokeOpacity="1" strokeWidth="2" />
              <line x1="780"  y1="60"  x2="780"  y2="65"  strokeOpacity="1" strokeWidth="2" />
              <line x1="780"  y1="75"  x2="780"  y2="80"  strokeOpacity="1" strokeWidth="2" />
              <line x1="780"  y1="90"  x2="780"  y2="95"  strokeOpacity="1" strokeWidth="2" />
              <line x1="1260" y1="75"  x2="1260" y2="80"  strokeOpacity="1" strokeWidth="2" />
              <line x1="1260" y1="90"  x2="1260" y2="95"  strokeOpacity="1" strokeWidth="2" />
            </g>
          </svg>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <Reveal direction="up" duration={1200} distance={60} delay={150}>
                <p className="text-sm tracking-[0.35em] uppercase text-blue-200 mb-5">
                  Cyprus • Greece • Middle East • Europe
                </p>

                <h1 className="text-5xl lg:text-7xl font-serif font-bold leading-[1.05] mb-8">
                  Let Sales
                  <br />
                  <span className="text-[#9fc6ff]">Lead the way</span>
                </h1>

                <p className="text-lg text-neutral-300 leading-relaxed max-w-2xl mb-8">
                  RAL Real Advisor helps real estate developers build
                  predictable, scalable and high-performance commercial systems
                  through commercial optimisation, GTM execution and pricing
                  strategy.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="#contact"
                    className="bg-[#9fc6ff] text-[#05070d] px-8 py-4 font-medium tracking-wide hover:opacity-90 transition"
                  >
                    Book a Consultation
                  </a>
                  <a
                    href="#services"
                    className="border border-white/20 px-8 py-4 font-medium tracking-wide hover:bg-white/10 transition"
                  >
                    Explore Services
                  </a>
                </div>

                <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
                  <div className="border-l border-[#9fc6ff]/40 pl-4">
                    <div className="text-white font-medium">20+ Years</div>
                    <div className="text-neutral-400">
                      Commercial leadership experience
                    </div>
                  </div>
                  <div className="border-l border-[#9fc6ff]/40 pl-4">
                    <div className="text-white font-medium">€800M+</div>
                    <div className="text-neutral-400">Real estate sales led</div>
                  </div>
                  <div className="border-l border-[#9fc6ff]/40 pl-4">
                    <div className="text-white font-medium">Structured</div>
                    <div className="text-neutral-400">
                      Optimisation Model
                    </div>
                  </div>
                </div>
              </Reveal>

              <Reveal direction="left" duration={1100} distance={50} delay={400}>
                <div className="bg-neutral-800/50 border border-neutral-700/50 p-8 lg:p-10">
                  <div className="flex items-center gap-4 mb-8">
                    <img
                      src="http://agency.hubst3r.com/wp-content/uploads/2026/03/logo.png"
                      alt="RAL Real Advisor"
                      className="w-16 h-16 object-contain"
                    />
                    <div>
                      <div className="text-sm uppercase tracking-[0.25em] text-blue-200">
                        RAL Commercial
                      </div>
                      <div className="text-neutral-400 text-sm mt-1">
                        Advisory + execution-led commercial system
                      </div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    {engineItems.map((item, idx) => (
                      <Reveal
                        key={item}
                        direction="up"
                        duration={600}
                        distance={20}
                        delay={700 + idx * 80}
                      >
                        <div className="border border-neutral-700/60 bg-neutral-900/60 px-4 py-4 text-sm text-neutral-200 flex items-center justify-between gap-3">
                          <span className="flex-1">{item}</span>
                          <span className="w-6 h-6 shrink-0">
                            {pillarIconsLite[idx]}
                          </span>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Who we are — 2×2 grid of subsections on desktop, with OUR MISSION
            spanning full width below. Stacks to a single column on mobile.
            Plain typographic blocks (no card chrome) — eyebrow, heading, body,
            mirroring the FOUNDER section's style. */}
        <section className="py-16 bg-neutral-100 border-y border-neutral-200 text-neutral-900">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">

            {/* 2×2 grid of the four explanatory subsections */}
            <div className="grid md:grid-cols-2 gap-x-12 lg:gap-x-16 gap-y-12">

              {/* WHO WE ARE */}
              <Reveal direction="up" duration={800}>
                <div>
                  <p className="text-xs tracking-[0.35em] uppercase text-[#7eaee8] mb-3">
                    Who We Are
                  </p>
                  <h2 className="text-xl lg:text-2xl font-serif font-bold mb-4 leading-snug">
                    A commercial advisory firm built to drive real estate sales performance at scale
                  </h2>
                  <div className="space-y-3 text-neutral-700 leading-relaxed text-sm">
                    <p>
                      At RAL Real Advisor Ltd, we are a commercial advisory
                      firm built for one purpose: to drive real estate sales
                      performance at scale.
                    </p>
                    <p>
                      We work with developers, real estate companies, investors
                      and commercial teams to transform how they position,
                      structure, and execute sales — turning projects into
                      high-performing revenue engines.
                    </p>
                    <p>
                      Unlike traditional consultants, we do not operate at a
                      distance. We embed ourselves inside the commercial
                      function and take a hands-on role in shaping outcomes,
                      from strategy to execution.
                    </p>
                  </div>
                </div>
              </Reveal>

              {/* OUR APPROACH */}
              <Reveal direction="up" duration={800} delay={100}>
                <div>
                  <p className="text-xs tracking-[0.35em] uppercase text-[#7eaee8] mb-3">
                    Our Approach
                  </p>
                  <h3 className="text-xl lg:text-2xl font-serif font-bold mb-4 leading-snug">
                    Let sales lead the way
                  </h3>
                  <div className="space-y-3 text-neutral-700 leading-relaxed text-sm">
                    <p>Our philosophy is simple.</p>
                    <p>
                      Every decision — from pricing and product design to
                      marketing and partnerships — should serve one objective:
                      faster, stronger, and more predictable sales.
                    </p>
                    <p>We combine:</p>
                    <ul className="space-y-1.5 ml-4 list-disc marker:text-[#7eaee8]">
                      <li>
                        <span className="font-medium text-neutral-900">
                          Strategic clarity
                        </span>{" "}
                        — positioning, pricing, market fit
                      </li>
                      <li>
                        <span className="font-medium text-neutral-900">
                          Commercial structure
                        </span>{" "}
                        — processes, CRM, team architecture
                      </li>
                      <li>
                        <span className="font-medium text-neutral-900">
                          Execution discipline
                        </span>{" "}
                        — KPIs, pipelines, deal conversion
                      </li>
                    </ul>
                    <p>
                      This creates a system where sales is not reactive — but
                      engineered.
                    </p>
                  </div>
                </div>
              </Reveal>

              {/* OUR EXPERIENCE */}
              <Reveal direction="up" duration={800} delay={100}>
                <div>
                  <p className="text-xs tracking-[0.35em] uppercase text-[#7eaee8] mb-3">
                    Our Experience
                  </p>
                  <h3 className="text-xl lg:text-2xl font-serif font-bold mb-4 leading-snug">
                    20+ years across Cyprus, South-Eastern Europe and the Eastern Mediterranean
                  </h3>
                  <div className="space-y-3 text-neutral-700 leading-relaxed text-sm">
                    <p>
                      RAL is led by senior commercial leadership with over 20
                      years of real estate experience across Cyprus,
                      South-Eastern Europe, and the Eastern Mediterranean.
                    </p>
                    <p>Our track record includes:</p>
                    <ul className="space-y-1.5 ml-4 list-disc marker:text-[#7eaee8]">
                      <li>Over €800 million in real estate transactions</li>
                      <li>Cross-border deal structuring and investor sourcing</li>
                      <li>
                        Advisory on large-scale residential, mixed-use
                        developments and real estate agents
                      </li>
                      <li>
                        Deep expertise in luxury real estate and
                        investment-driven markets
                      </li>
                    </ul>
                  </div>
                </div>
              </Reveal>

              {/* WHAT MAKES US DIFFERENT */}
              <Reveal direction="up" duration={800} delay={150}>
                <div>
                  <p className="text-xs tracking-[0.35em] uppercase text-[#7eaee8] mb-3">
                    What Makes Us Different
                  </p>
                  <h3 className="text-xl lg:text-2xl font-serif font-bold mb-4 leading-snug">
                    Most advisors deliver reports. We build commercial machines.
                  </h3>
                  <div className="space-y-3 text-neutral-700 leading-relaxed text-sm">
                    <ul className="space-y-1.5 ml-4 list-disc marker:text-[#7eaee8]">
                      <li>We connect strategy to revenue, not theory</li>
                      <li>We design systems that teams can actually execute</li>
                      <li>We focus on conversion, not just visibility</li>
                      <li>We stay involved until results are delivered</li>
                    </ul>
                  </div>
                </div>
              </Reveal>

            </div>

            {/* OUR MISSION — full-width below the grid, plain typographic block */}
            <Reveal direction="up" duration={900} delay={200}>
              <div className="mt-12 pt-10 border-t border-neutral-300">
                <p className="text-xs tracking-[0.35em] uppercase text-[#7eaee8] mb-4">
                  Our Mission
                </p>
                <p className="text-lg lg:text-xl font-serif font-medium leading-relaxed text-neutral-900 max-w-3xl">
                  To help real estate companies unlock their full sales
                  potential through structured, disciplined, and scalable
                  commercial execution.
                </p>
              </div>
            </Reveal>

          </div>
        </section>

        {/* Services / engine - NOW DARK */}
        <section
          id="services"
          className="py-20 bg-[#05070d] text-white border-t border-white/10 scroll-mt-28"
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <Reveal direction="up" duration={800}>
              <div className="text-center mb-14">
                <p className="text-sm tracking-[0.35em] uppercase text-[#9fc6ff] mb-4">
                  Core Commercial Engine
                </p>
                <h2 className="text-4xl lg:text-5xl font-serif font-bold">
                  Eight commercial pillars
                </h2>
              </div>
            </Reveal>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
              {engineItems.map((item, index) => (
                <Reveal
                  key={item}
                  direction="up"
                  duration={700}
                  distance={30}
                  delay={index * 90}
                >
                  <div className="bg-[#05070d] p-6 min-h-[180px] hover:bg-neutral-900 transition h-full group">
                    <div className="flex items-center justify-between mb-5">
                      <div className="text-[#9fc6ff] text-sm tracking-[0.25em] uppercase">
                        0{index + 1}
                      </div>
                      <div
                        className="relative w-10 h-10"
                        style={{
                          filter: "drop-shadow(0 0 6px rgba(159,198,255,0.45))",
                        }}
                      >
                        <div
                          className="absolute inset-0 rounded-full"
                          style={{
                            background:
                              "radial-gradient(circle at 50% 50%, rgba(159,198,255,0.35) 0%, rgba(159,198,255,0.12) 40%, transparent 70%)",
                          }}
                        />
                        <div className="relative w-full h-full">
                          {pillarIcons[index]}
                        </div>
                      </div>
                    </div>
                    <h3 className="text-xl font-serif font-semibold text-white">
                      {item}
                    </h3>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* 7 phase system */}
        <section
          id="system"
          className="py-20 bg-neutral-100 border-t border-b border-neutral-200 text-neutral-900 scroll-mt-28"
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-2 gap-14 items-start">
              <Reveal direction="up" duration={800}>
                <p className="text-sm tracking-[0.35em] uppercase text-[#7eaee8] mb-4">
                  The 7-Phase Commercial System
                </p>
                <h2 className="text-4xl lg:text-5xl font-serif font-bold mb-6">
                  Structured optimisation from strategy to execution
                </h2>
                <p className="text-neutral-600 text-lg leading-relaxed">
                  The process moves from strategy and market intelligence through
                  marketing, sales enablement, CRM governance, pricing, GTM
                  execution and optimisation.                
                </p>
              </Reveal>

              <div className="space-y-4">
                {systemPhases.map((phase, index) => (
                  <Reveal
                    key={phase}
                    direction="left"
                    duration={650}
                    distance={30}
                    delay={200 + index * 90}
                  >
                    <div className="flex items-center gap-5 border-b border-neutral-200 pb-4">
                      <div className="text-2xl font-serif text-[#7eaee8] w-10">
                        {index + 1}
                      </div>
                      <div className="text-neutral-700">{phase}</div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* What we Optimise */}
        <section className="py-20 bg-[#05070d] text-white border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <Reveal direction="up" duration={800}>
              <div className="text-center mb-14">
                <p className="text-sm tracking-[0.35em] uppercase text-[#9fc6ff] mb-4">
                  What We Optimise
                </p>
                <h2 className="text-4xl lg:text-5xl font-serif font-bold">
                  These are the areas our advisory is addressing
                </h2>
              </div>
            </Reveal>

            <div className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                {transformItems.slice(0, 3).map((item, idx) => (
                  <Reveal
                    key={item}
                    direction="up"
                    duration={650}
                    distance={25}
                    delay={150 + idx * 100}
                  >
                    <div className="border border-white/10 bg-white/5 px-6 py-6 text-neutral-200 h-full">
                      {item}
                    </div>
                  </Reveal>
                ))}
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {transformItems.slice(3).map((item, idx) => (
                  <Reveal
                    key={item}
                    direction="up"
                    duration={650}
                    distance={25}
                    delay={450 + idx * 100}
                  >
                    <div className="border border-white/10 bg-white/5 px-6 py-6 text-neutral-200 h-full">
                      {item}
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Diagnostic tool */}
        <section className="py-20 bg-[#05070d] text-white border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-14 items-center">
            <Reveal direction="up" duration={800}>
              <p className="text-sm tracking-[0.35em] uppercase text-[#9fc6ff] mb-4">
                Commercial Diagnostic Tool
              </p>
              <h2 className="text-4xl lg:text-5xl font-serif font-bold mb-6">
                Diagnose maturity. Map priorities. Build the plan and execute it.
              </h2>
              <p className="text-neutral-300 text-lg leading-relaxed mb-8">
                The diagnostic covers strategy, marketing, sales, CRM, team,
                pricing, BD and agents, producing a maturity score, radar chart,
                RAL service mapping, planning and execution.
              </p>

              
              <Link
                to="/diagnostic"
                className="inline-block bg-[#9fc6ff] text-[#05070d] px-8 py-4 font-medium tracking-wide hover:opacity-90 transition"
              >
                Explore the Diagnostic
              </Link>
            </Reveal>

            <Reveal direction="left" duration={900} distance={40} delay={250}>
              <SpiderChart />
            </Reveal>
          </div>
        </section>

        {/* Deliverables + work */}
        <section className="py-20 bg-white text-neutral-900 border-t border-b border-neutral-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12">
            <div>
              <Reveal direction="up" duration={800}>
                <p className="text-sm tracking-[0.35em] uppercase text-[#7eaee8] mb-4">
                  Our Deliverables
                </p>
                <h2 className="text-3xl lg:text-4xl font-serif font-bold mb-8">
                  Tangible outputs for commercial performance
                </h2>
              </Reveal>
              <div className="space-y-4">
                {deliverables.map((item, idx) => (
                  <Reveal
                    key={item}
                    direction="right"
                    duration={650}
                    distance={30}
                    delay={200 + idx * 90}
                  >
                    <div className="border border-neutral-200 bg-neutral-50 px-5 py-5 text-neutral-700">
                      {item}
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            <div>
              <Reveal direction="up" duration={800} delay={150}>
                <p className="text-sm tracking-[0.35em] uppercase text-[#7eaee8] mb-4">
                  How We Work
                </p>
                <h2 className="text-3xl lg:text-4xl font-serif font-bold mb-8">
                  Embedded support across leadership, funnel and CRM
                </h2>
              </Reveal>
              <div className="space-y-4">
                {workItems.map((item, idx) => (
                  <Reveal
                    key={item}
                    direction="left"
                    duration={650}
                    distance={30}
                    delay={350 + idx * 90}
                  >
                    <div className="border border-neutral-200 bg-neutral-50 px-5 py-5 text-neutral-700">
                      {item}
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Engagement models - NOW DARK */}
        <section className="py-20 bg-[#05070d] text-white border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <Reveal direction="up" duration={800}>
              <div className="text-center mb-14">
                <p className="text-sm tracking-[0.35em] uppercase text-[#9fc6ff] mb-4">
                  Engagement Models
                </p>
                <h2 className="text-4xl lg:text-5xl font-serif font-bold">
                  Flexible ways to work together
                </h2>
              </div>
            </Reveal>

            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
              {engagementModels.map((item, idx) => (
                <Reveal
                  key={item}
                  direction="up"
                  duration={650}
                  distance={25}
                  delay={150 + idx * 100}
                >
                  <div className="border border-white/10 bg-white/5 p-6 text-center text-neutral-200 min-h-[130px] flex items-center justify-center h-full">
                    {item}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Founder */}
        <section
          id="founder"
          className="py-20 bg-white border-t border-neutral-200 text-neutral-900 scroll-mt-28"
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-[1.1fr_0.9fr] gap-14 items-start">
            <div>
              <Reveal direction="up" duration={800}>
                <p className="text-sm tracking-[0.35em] uppercase text-[#7eaee8] mb-4">
                  Founder
                </p>
                <h2 className="text-4xl lg:text-5xl font-serif font-bold mb-6">
                  George Englezos
                </h2>
                <p className="text-neutral-600 text-lg leading-relaxed mb-8">
                  Founder of RAL Real Advisor Ltd, with more than 20 years of real
                  estate commercial leadership experience across Cyprus and
                  South-Eastern Europe.
                </p>
              </Reveal>

              <div className="grid gap-4">
                {[
                  "Former Chief Commercial Officer (CCO) of Limassol Greens Golf Resort",
                  "Managing sales of Limassol Marina for 8 years",
                  "Led €800M+ in real estate sales across large-scale residential, mixed-use and master-planned developments",
                  "Specialist in GTM strategy, commercial optimisation, pricing architecture and sales performance",
                  "Deep expertise with HNWI buyers, investment-only segments and international relocation markets",
                  "Proven capability building high-performance sales teams, agent networks and CRM-driven commercial systems",
                ].map((item, idx) => (
                  <Reveal
                    key={item}
                    direction="right"
                    duration={650}
                    distance={30}
                    delay={300 + idx * 90}
                  >
                    <div className="border border-neutral-200 bg-neutral-50 px-5 py-5 text-neutral-700">
                      {item}
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            <Reveal direction="left" duration={900} distance={40} delay={400}>
              <div className="bg-neutral-50 border border-neutral-200 p-8">
                <div className="text-sm uppercase tracking-[0.25em] text-[#7eaee8] mb-4">
                  Why Developers Choose RAL
                </div>
                <div className="space-y-4">
                  {[
                    "No Commission",
                    "Advisory + Execution",
                    "Improved conversions & pricing",
                    "Strong dashboards & KPIs",
                    "Experience with investor & premium markets",
                    "Commercial Optimisation",
                  ].map((item) => (
                    <div
                      key={item}
                      className="border-b border-neutral-200 pb-4 text-neutral-700"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* CTA / contact */}
        <section
          id="contact"
          className="py-24 bg-neutral-900 text-white relative overflow-hidden scroll-mt-28"
        >
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
                backgroundSize: "40px 40px",
              }}
            />
          </div>

          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center relative">
            <Reveal direction="up" duration={900} distance={30}>
              <p className="text-sm tracking-[0.35em] uppercase text-blue-200 mb-4">
                Let’s Talk
              </p>
              <h2 className="text-4xl lg:text-5xl font-serif font-bold mb-6">
                Ready to strengthen your commercial engine?
              </h2>
              <p className="text-lg text-neutral-300 max-w-2xl mx-auto leading-relaxed mb-10">
                RAL Real Advisor supports developers across Cyprus, Greece, the
                Middle East and Europe with commercial optimisation, GTM
                execution and pricing strategy.
              </p>
            </Reveal>

            <Reveal direction="up" duration={700} distance={20} delay={300}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
                <a
                  href="tel:+35799277774"
                  className="bg-[#9fc6ff] text-[#05070d] px-8 py-4 font-medium tracking-wide hover:opacity-90 transition"
                >
                  Call +357 99277774
                </a>
                <a
                  href="mailto:george@ralrealadvisor.cy"
                  className="border border-white/20 px-8 py-4 font-medium tracking-wide hover:bg-white/10 transition"
                >
                  george@ralrealadvisor.cy
                </a>
              </div>
            </Reveal>

            <div className="text-neutral-500 text-sm">
              RAL Real Advisor Ltd
            </div>
          </div>
        </section>

        <BackToTop />
      </div>
    </>
  );
};

export default AgencyLandingPage;